import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useAgGridApi } from "tap/components/AGGrid/hooks.js";
import { useTPRExceptionsGridUtils } from "./utils/gridUtils";
import {
  useTPRExceptionsData,
  beginTprExceptionsFetch,
  isLatestTprExceptionsFetch,
} from "../../hooks/useTPRExceptionsData";
import { buildTprExceptionsPageUpdate } from "../../utils/paginationUtils";
import { buildDismissByRowFromSession } from "../../utils/userOptionsUtils.js";
import DataGrid from "@dtci/spellbook/DataGrid";
import styles from "../../../../components/AGGrid/details-table.module.scss";

/**
 * TPR Exceptions Grid - Displays TPR exceptions with dismiss action.
 */
export const TPRExceptionsGrid = ({ onDismiss }) => {
  const agGridApi = useAgGridApi();
  const { tprExceptions, refreshTPRExceptions, currentPage, tprExceptionsRange, totalItems } =
    useSelector((s) => s.tprExceptions);
  const {
    fetchTPRExceptions,
    syncUserOptions,
    updateTPRExceptions,
    filterForm,
    itemsPerPage,
  } = useTPRExceptionsData();

  const { columns } = useTPRExceptionsGridUtils({
    columnHandlers: [{ onDismiss }],
  });

  const gridRef = agGridApi.AgGridApiRef;

  const refreshList = async () => {
    const fetchId = beginTprExceptionsFetch();
    const data = await fetchTPRExceptions(tprExceptionsRange, filterForm);

    if (data && isLatestTprExceptionsFetch(fetchId)) {
      updateTPRExceptions(
        buildTprExceptionsPageUpdate(data, {
          itemsPerPage,
          range: tprExceptionsRange,
          previousTotalItems: totalItems,
          preserveTotal: false,
        }),
      );
    }

    const dismissByRow = buildDismissByRowFromSession();
    const exceptionRows = data?.content ?? [];
    await syncUserOptions(
      dismissByRow ? [...exceptionRows, dismissByRow] : exceptionRows,
    );
  };

  useEffect(() => {
    if (!refreshTPRExceptions) return;

    const runRefresh = async () => {
      await refreshList();
    };

    runRefresh();
  }, [refreshTPRExceptions]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    api.setGridOption("rowData", tprExceptions ?? []);
    api.refreshClientSideRowModel("group");
  }, [tprExceptions, currentPage, tprExceptionsRange]);

  return (
    <>
      <DataGrid
        key={`tpr-exceptions-${currentPage}-${itemsPerPage}-${tprExceptionsRange}`}
        instanceId={"tprExceptionsGrid"}
        className={styles.dataGrid}
        autoSizing
        agGrid={{
          gridOptions: {
            groupDefaultExpanded: 1,
          },
          ref: gridRef,
          rowData: tprExceptions ?? [],
          suppressColumnStateReset: true,
          headerHeight: 45,
          context: { onDismiss, gridRef },
          getRowId: (params) => String(params.data.id),
          suppressScrollOnNewData: true,
        }}
        bordered="none"
        columnDefs={columns}
        defaultColumnDef={{
          resizable: true,
          wrapText: true,
          autoHeight: true,
          flex: 1,
          minWidth: 100,
          suppressHeaderMenuButton: true,
          cellStyle: {
            paddingTop: "10px",
            paddingBottom: "10px",
            wordBreak: "break-word",
          },
        }}
        sortable={false}
        autoHeight={false}
        striped
        noResultsMessage={"No TPR Exceptions Found"}
      />
    </>
  );
};
